/**
 * AdminPage — composed page with role guard that displays system configuration,
 * user management, and IdP status for site administrators.
 *
 * Non-admin users are redirected silently to /boards.
 */

import React, { useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { isAdmin } from '../services/jwt-utils';
import { AdminProvider, useAdmin } from '../state/admin-context';
import { SystemStatusBanner } from '../components/admin/SystemStatusBanner';
import { IdpConfigWizard } from '../components/admin/IdpConfigWizard';
import { IdpManagementPanel } from '../components/admin/IdpManagementPanel';
import { SystemSettingsForm } from '../components/admin/SystemSettingsForm';
import { UserManagementTable } from '../components/admin/UserManagementTable';
import { McpKeyWorklist } from '../components/admin/McpKeyWorklist';
import { AdminNotificationConfig } from '../components/admin/AdminNotificationConfig';
import { PipelineDashboard } from '../components/admin/PipelineDashboard';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';

// ---------------------------------------------------------------------------
// Role guard
// ---------------------------------------------------------------------------

export function AdminPage(): React.ReactElement {
  if (!isAdmin()) {
    return <Navigate to="/boards" replace />;
  }

  return (
    <AdminProvider>
      <AdminContent />
    </AdminProvider>
  );
}

// ---------------------------------------------------------------------------
// Inner content
// ---------------------------------------------------------------------------

function AdminContent(): React.ReactElement {
  const { state, fetchSettings, fetchUsers, updateSettings, updateUser } = useAdmin();

  const handleIdpConfigComplete = useCallback((): void => {
    fetchSettings();
  }, [fetchSettings]);

  const handleUpdateUser = useCallback(
    async (userId: string, updates: { role?: 'admin' | 'user'; enabled?: boolean }): Promise<void> => {
      await updateUser(userId, updates);
    },
    [updateUser],
  );

  const handleRetryUsers = useCallback((): void => {
    fetchUsers();
  }, [fetchUsers]);

  // Loading state
  if (state.isLoading && !state.settings && state.users.length === 0) {
    return (
      <main className="min-h-screen p-6" role="main" aria-busy="true">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading admin settings">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
            <span className="ml-3 text-muted-foreground">Loading admin settings…</span>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (state.error && !state.settings && state.users.length === 0) {
    return (
      <main className="min-h-screen p-6" role="main">
        <div className="mx-auto max-w-5xl">
          <Alert variant="destructive">
            <AlertTitle>Error loading admin settings</AlertTitle>
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
          <Button className="mt-4" onClick={() => { fetchSettings(); fetchUsers(); }}>
            Retry
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen p-6" role="main">
      <div className="mx-auto max-w-5xl space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Administration</h1>

        {/* System Status — Bootstrap mode banner */}
        {state.bootstrapMode && (
          <Card>
            <CardHeader>
              <h2 id="admin-status" className="text-2xl font-semibold leading-none tracking-tight">System Status</h2>
            </CardHeader>
            <CardContent>
              <SystemStatusBanner bootstrapMode={state.bootstrapMode} />
            </CardContent>
          </Card>
        )}

        {/* IdP Configuration Wizard — shown in bootstrap AND configured mode for management */}
        <Card>
          <CardHeader>
            <h2 id="admin-idp-config" className="text-2xl font-semibold leading-none tracking-tight">Identity Provider Configuration</h2>
          </CardHeader>
          <CardContent>
            {state.bootstrapMode ? (
              <IdpConfigWizard onComplete={handleIdpConfigComplete} />
            ) : (
              <IdpManagementPanel onUpdate={handleIdpConfigComplete} />
            )}
          </CardContent>
        </Card>

        {/* Non-critical error */}
        {state.error && state.settings && (
          <Alert variant="destructive">
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        )}

        {/* Two-column grid for settings and users */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* System Settings */}
          {state.settings && (
            <Card>
              <CardHeader>
                <h2 id="admin-settings" className="text-2xl font-semibold leading-none tracking-tight">System Settings</h2>
              </CardHeader>
              <CardContent>
                <SystemSettingsForm
                  settings={state.settings}
                  onUpdateSettings={updateSettings}
                  error={null}
                />
              </CardContent>
            </Card>
          )}

          {/* User Management */}
          <Card>
            <CardHeader>
              <h2 id="admin-users" className="text-2xl font-semibold leading-none tracking-tight">User Management</h2>
            </CardHeader>
            <CardContent>
              <UserManagementTable
                users={state.users}
                onUpdateUser={handleUpdateUser}
                error={null}
                onRetry={handleRetryUsers}
              />
            </CardContent>
          </Card>
        </div>

        {/* MCP Keys worklist */}
        <Card>
          <CardHeader>
            <h2 id="admin-mcp-keys" className="text-2xl font-semibold leading-none tracking-tight">MCP Keys</h2>
          </CardHeader>
          <CardContent>
            <McpKeyWorklist />
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <h2 id="admin-notifications" className="text-2xl font-semibold leading-none tracking-tight">Notifications</h2>
          </CardHeader>
          <CardContent>
            <AdminNotificationConfig />
          </CardContent>
        </Card>

        {/* AI Pipeline */}
        <Card>
          <CardHeader>
            <h2 id="admin-ai-pipeline" className="text-2xl font-semibold leading-none tracking-tight">AI Pipeline</h2>
          </CardHeader>
          <CardContent>
            <PipelineDashboard />
          </CardContent>
        </Card>

        {/* IdP Status */}
        {state.idpStatus && (
          <Card>
            <CardHeader>
              <h2 id="admin-idp-status" className="text-2xl font-semibold leading-none tracking-tight">Identity Provider Status</h2>
            </CardHeader>
            <CardContent>
              <IdpStatusDisplay />
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// IdP Status display
// ---------------------------------------------------------------------------

function IdpStatusDisplay(): React.ReactElement {
  const { state } = useAdmin();
  const idp = state.idpStatus;

  if (!idp) return <></>;

  const statusColor = idp.status === 'connected' ? 'text-success' : 'text-destructive';
  const statusLabel = idp.status.charAt(0).toUpperCase() + idp.status.slice(1);

  return (
    <dl className="space-y-2 text-sm">
      <div className="flex gap-2">
        <dt className="font-semibold">Provider Type:</dt>
        <dd>{idp.type}</dd>
      </div>
      <div className="flex gap-2">
        <dt className="font-semibold">Endpoint:</dt>
        <dd><code className="text-xs bg-muted px-1 py-0.5 rounded">{idp.endpoint}</code></dd>
      </div>
      <div className="flex gap-2">
        <dt className="font-semibold">Status:</dt>
        <dd className={statusColor}>{statusLabel}</dd>
      </div>
    </dl>
  );
}
